/**
 * Normalize text for native PDF export using ONLY built-in PDF fonts.
 *
 * Times-Roman (WinAnsiEncoding) covers:
 *   - Full Latin-1 (U+0000–U+00FF): ±  ×  ÷  °  µ  ©  ®  «  »  etc.
 *   - Windows-1252 extras: –  —  '  '  "  "  •  …  ™  †  ‡  ‰  €
 *   - A few additional Latin Extended: Œ  œ  Š  š  Ž  ž  Ÿ
 *
 * Everything else is mapped to a readable ASCII/Latin-1 equivalent so PDFKit
 * never sees an unmapped glyph that could produce corrupt glyph coordinates.
 */

// ---------------------------------------------------------------------------
// Named HTML entity → safe character (Latin-1/WinAnsi or ASCII fallback)
// ---------------------------------------------------------------------------
const HTML_ENTITY_MAP = {
  // Math / comparison — preserve Unicode glyphs (rendered via Noto Sans in PDF)
  '&ge;': '\u2265', '&greaterequal;': '\u2265', '&#8805;': '\u2265', '&#x2265;': '\u2265',
  '&le;': '\u2264', '&lessequal;': '\u2264', '&#8804;': '\u2264', '&#x2264;': '\u2264',
  '&ne;': '\u2260', '&notequal;': '\u2260', '&#8800;': '\u2260', '&#x2260;': '\u2260',
  '&asymp;': '~', '&#8776;': '~', '&#x2248;': '~',
  '&minus;': '-', '&#8722;': '-', '&#x2212;': '-',
  '&sum;': 'Sum', '&#8721;': 'Sum',
  '&infin;': 'inf', '&#8734;': 'inf',
  '&prop;': 'prop.', '&#8733;': 'prop.',
  '&sim;': '~', '&#8764;': '~',
  '&sdot;': '\u00B7', '&#8901;': '\u00B7',  // middle dot (in Latin-1)
  '&times;': '\u00D7', '&#215;': '\u00D7', '&#xd7;': '\u00D7',  // × in Latin-1
  '&divide;': '\u00F7', '&#247;': '\u00F7', '&#xf7;': '\u00F7',  // ÷ in Latin-1
  '&plusmn;': '\u00B1', '&#177;': '\u00B1', '&#xb1;': '\u00B1',  // ± in Latin-1
  '&deg;': '\u00B0', '&#176;': '\u00B0', '&#xb0;': '\u00B0',    // ° in Latin-1
  '&mu;': '\u00B5', '&#181;': '\u00B5', '&#xb5;': '\u00B5',    // µ in Latin-1
  '&micro;': '\u00B5',
  '&sqrt;': 'sqrt', '&#8730;': 'sqrt',
  '&int;': 'integral', '&#8747;': 'integral',
  '&forall;': 'for all', '&#8704;': 'for all',
  '&exist;': 'exists', '&#8707;': 'exists',
  '&empty;': 'empty', '&#8709;': 'empty',
  '&nabla;': 'nabla', '&#8711;': 'nabla',
  '&isin;': 'in', '&#8712;': 'in',
  '&notin;': 'not in', '&#8713;': 'not in',
  '&ni;': 'contains', '&#8715;': 'contains',
  '&and;': 'and', '&#8743;': 'and',
  '&or;': 'or', '&#8744;': 'or',
  '&cap;': 'intersect', '&#8745;': 'intersect',
  '&cup;': 'union', '&#8746;': 'union',
  '&sub;': 'subset', '&#8834;': 'subset',
  '&sup;': 'superset', '&#8835;': 'superset',
  // Arrows — ASCII fallbacks
  '&rarr;': '->', '&rightarrow;': '->', '&#8594;': '->', '&#x2192;': '->',
  '&larr;': '<-', '&#8592;': '<-',
  '&darr;': 'v', '&#8595;': 'v',
  '&uarr;': '^', '&#8593;': '^',
  '&harr;': '<->', '&#8596;': '<->',
  '&hArr;': '<=>', '&#8660;': '<=>',
  '&rArr;': '=>', '&#8658;': '=>',
  '&lArr;': '<=', '&#8656;': '<=',
  // Dashes / spaces — WinAnsi covers en/em dash
  '&ndash;': '\u2013', '&#8211;': '\u2013', '&#x2013;': '\u2013',
  '&mdash;': '\u2014', '&#8212;': '\u2014', '&#x2014;': '\u2014',
  '&nbsp;': ' ', '&#160;': ' ', '&#xa0;': ' ',
  '&thinsp;': ' ', '&#8201;': ' ',
  '&ensp;': ' ', '&#8194;': ' ',
  '&emsp;': ' ', '&#8195;': ' ',
  // Smart quotes — WinAnsi covers these
  '&lsquo;': '\u2018', '&rsquo;': '\u2019', '&sbquo;': '\u201A',
  '&#8216;': '\u2018', '&#8217;': '\u2019', '&#8218;': '\u201A',
  '&ldquo;': '\u201C', '&rdquo;': '\u201D', '&bdquo;': '\u201E',
  '&#8220;': '\u201C', '&#8221;': '\u201D', '&#8222;': '\u201E',
  // Greek — ASCII name fallbacks (Times-Roman has no Greek)
  '&alpha;': 'alpha', '&Alpha;': 'Alpha',
  '&beta;': 'beta', '&Beta;': 'Beta',
  '&gamma;': 'gamma', '&Gamma;': 'Gamma',
  '&delta;': 'delta', '&Delta;': 'Delta',
  '&epsilon;': 'epsilon', '&Epsilon;': 'Epsilon',
  '&zeta;': 'zeta', '&Zeta;': 'Zeta',
  '&eta;': 'eta', '&Eta;': 'Eta',
  '&theta;': 'theta', '&Theta;': 'Theta',
  '&iota;': 'iota', '&Iota;': 'Iota',
  '&kappa;': 'kappa', '&Kappa;': 'Kappa',
  '&lambda;': 'lambda', '&Lambda;': 'Lambda',
  '&nu;': 'nu', '&Nu;': 'Nu',
  '&xi;': 'xi', '&Xi;': 'Xi',
  '&pi;': 'pi', '&Pi;': 'Pi',
  '&rho;': 'rho', '&Rho;': 'Rho',
  '&sigma;': 'sigma', '&Sigma;': 'Sigma',
  '&tau;': 'tau', '&Tau;': 'Tau',
  '&upsilon;': 'upsilon', '&Upsilon;': 'Upsilon',
  '&phi;': 'phi', '&Phi;': 'Phi',
  '&chi;': 'chi', '&Chi;': 'Chi',
  '&psi;': 'psi', '&Psi;': 'Psi',
  '&omega;': 'omega', '&Omega;': 'Omega',
  // Misc — WinAnsi covers most of these
  '&middot;': '\u00B7', '&#183;': '\u00B7',
  '&bull;': '\u2022', '&#8226;': '\u2022',
  '&copy;': '\u00A9', '&#169;': '\u00A9',
  '&reg;': '\u00AE', '&#174;': '\u00AE',
  '&trade;': '\u2122', '&#8482;': '\u2122',
  '&ellipsis;': '\u2026', '&hellip;': '\u2026', '&#8230;': '\u2026',
  '&lt;': '<', '&gt;': '>', '&amp;': '&', '&quot;': '"', '&apos;': "'",
  '&laquo;': '\u00AB', '&raquo;': '\u00BB',
  '&sect;': '\u00A7', '&#167;': '\u00A7',
  '&para;': '\u00B6', '&#182;': '\u00B6',
  '&dagger;': '\u2020', '&#8224;': '\u2020',
  '&Dagger;': '\u2021', '&#8225;': '\u2021',
  '&permil;': '\u2030', '&#8240;': '\u2030',
  '&prime;': "'", '&#8242;': "'",
  '&Prime;': "''", '&#8243;': "''",
  '&lfloor;': '[', '&rfloor;': ']',
  '&lceil;': '[', '&rceil;': ']',
};

const ENTITY_PATTERN = new RegExp(
  Object.keys(HTML_ENTITY_MAP)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'gi',
);

function decodeNumericEntity(entity) {
  const hex = entity.match(/&#x([0-9a-f]+);/i);
  if (hex) return String.fromCodePoint(parseInt(hex[1], 16));
  const dec = entity.match(/&#([0-9]+);/);
  if (dec) return String.fromCodePoint(parseInt(dec[1], 10));
  return entity;
}

function decodeRemainingNumericEntities(text) {
  return text.replace(/&#x([0-9a-f]+);|&#([0-9]+);/gi, (entity) =>
    decodeNumericEntity(entity),
  );
}

// ---------------------------------------------------------------------------
// Mojibake (Windows-1252 round-trip corruption)
// ---------------------------------------------------------------------------
const MOJIBAKE_MAP = {
  '\u0393\u00EB\u00D1': '\u2265',
  '\u0393\u00EB\u00D0': '\u2264',
  '\u0393\u00CB\u009C': '\u2260',
};

/** Glyphs routed to the embedded Unicode font (Noto Sans) in native PDF export. */
export const PDF_UNICODE_GLYPHS = new Set([
  0x2264, // ≤
  0x2265, // ≥
  0x2260, // ≠
  0x2248, // ≈
  0x2212, // −
]);

// ---------------------------------------------------------------------------
// Characters above Latin-1 that Times-Roman encodes via WinAnsiEncoding
// ---------------------------------------------------------------------------
const WINANSI_EXTRA_CODEPOINTS = new Set([
  0x2013, 0x2014, 0x2018, 0x2019, 0x201A, 0x201C, 0x201D, 0x201E,
  0x2020, 0x2021, 0x2022, 0x2026, 0x2030, 0x2039, 0x203A, 0x20AC, 0x2122,
  0x0152, 0x0153, 0x0160, 0x0161, 0x017D, 0x017E, 0x0178,
]);

/** Single-byte WinAnsi code points (Times-Roman built-in font). */
const WINANSI_SINGLE_BYTE = new Set();
for (let i = 0x20; i <= 0x7e; i += 1) WINANSI_SINGLE_BYTE.add(i);
for (let i = 0xa0; i <= 0xff; i += 1) WINANSI_SINGLE_BYTE.add(i);
// C1 slots undefined in WinAnsi — must never pass through
[0x81, 0x8d, 0x8f, 0x90, 0x9d, 0xad].forEach((c) => WINANSI_SINGLE_BYTE.delete(c));

// ---------------------------------------------------------------------------
// Unicode → ASCII/Latin-1 fallback map
// ---------------------------------------------------------------------------
const UNICODE_FALLBACK_MAP = {
  // Math operators (U+2200-U+22FF)
  '\u2200': 'for all', '\u2203': 'exists', '\u2205': 'empty',
  '\u2207': 'nabla',   '\u2208': 'in',     '\u2209': 'not in',
  '\u220B': 'contains','\u2212': '-',      '\u2215': '/',
  '\u2217': '*',       '\u2218': 'o',      '\u221A': 'sqrt',
  '\u221E': 'inf',     '\u2227': 'and',    '\u2228': 'or',
  '\u2229': 'intersect','\u222A': 'union', '\u222B': 'integral',
  '\u2248': '\u2248',       '\u2260': '\u2260',     '\u2261': '===',
  '\u2264': '\u2264',      '\u2265': '\u2265',     '\u2282': 'subset',
  '\u2283': 'superset','\u2295': '+',      '\u2297': 'x',
  '\u22C5': '.',
  // Arrows (U+2190-U+21FF)
  '\u2190': '<-', '\u2191': '^', '\u2192': '->', '\u2193': 'v',
  '\u2194': '<->', '\u21D0': '<=', '\u21D2': '=>', '\u21D4': '<=>',
  '\u21A6': '->', '\u2196': '<^', '\u2197': '^>', '\u2198': 'v>',  '\u2199': '<v',
  // Letterlike symbols (U+2100-U+214F)
  '\u2102': 'C', '\u210D': 'H', '\u2115': 'N', '\u2119': 'P',
  '\u211A': 'Q', '\u211D': 'R', '\u2124': 'Z', '\u2126': 'Ohm',
  '\u212B': 'A',
  // Greek (U+0370-U+03FF) — ASCII name
  '\u0391': 'Alpha',   '\u0392': 'Beta',    '\u0393': 'Gamma',
  '\u0394': 'Delta',   '\u0395': 'Epsilon', '\u0396': 'Zeta',
  '\u0397': 'Eta',     '\u0398': 'Theta',   '\u0399': 'Iota',
  '\u039A': 'Kappa',   '\u039B': 'Lambda',  '\u039C': 'Mu',
  '\u039D': 'Nu',      '\u039E': 'Xi',      '\u039F': 'Omicron',
  '\u03A0': 'Pi',      '\u03A1': 'Rho',     '\u03A3': 'Sigma',
  '\u03A4': 'Tau',     '\u03A5': 'Upsilon', '\u03A6': 'Phi',
  '\u03A7': 'Chi',     '\u03A8': 'Psi',     '\u03A9': 'Omega',
  '\u03B1': 'alpha',   '\u03B2': 'beta',    '\u03B3': 'gamma',
  '\u03B4': 'delta',   '\u03B5': 'epsilon', '\u03B6': 'zeta',
  '\u03B7': 'eta',     '\u03B8': 'theta',   '\u03B9': 'iota',
  '\u03BA': 'kappa',   '\u03BB': 'lambda',  '\u03BC': 'mu',
  '\u03BD': 'nu',      '\u03BE': 'xi',      '\u03BF': 'omicron',
  '\u03C0': 'pi',      '\u03C1': 'rho',     '\u03C3': 'sigma',
  '\u03C4': 'tau',     '\u03C5': 'upsilon', '\u03C6': 'phi',
  '\u03C7': 'chi',     '\u03C8': 'psi',     '\u03C9': 'omega',
  // Superscripts → ASCII
  '\u2070': '^0', '\u00B9': '^1', '\u00B2': '^2', '\u00B3': '^3',
  '\u2074': '^4', '\u2075': '^5', '\u2076': '^6', '\u2077': '^7',
  '\u2078': '^8', '\u2079': '^9',
  // Subscripts → ASCII
  '\u2080': '_0', '\u2081': '_1', '\u2082': '_2', '\u2083': '_3',
  '\u2084': '_4', '\u2085': '_5', '\u2086': '_6', '\u2087': '_7',
  '\u2088': '_8', '\u2089': '_9',
  // Vulgar fractions
  '\u00BC': '1/4', '\u00BD': '1/2', '\u00BE': '3/4',
  '\u2153': '1/3', '\u2154': '2/3', '\u2155': '1/5', '\u2156': '2/5',
  '\u2157': '3/5', '\u2158': '4/5', '\u2159': '1/6', '\u215A': '5/6',
  '\u215B': '1/8', '\u215C': '3/8', '\u215D': '5/8', '\u215E': '7/8',
  // Enclosed alphanumerics
  '\u24EA': '(0)', '\u2460': '(1)', '\u2461': '(2)', '\u2462': '(3)',
  '\u2463': '(4)', '\u2464': '(5)', '\u2465': '(6)', '\u2466': '(7)',
  '\u2467': '(8)', '\u2468': '(9)',
  // Ligatures
  '\uFB01': 'fi', '\uFB02': 'fl', '\uFB03': 'ffi', '\uFB04': 'ffl',
  // Roman numerals
  '\u2160': 'I',    '\u2161': 'II',   '\u2162': 'III', '\u2163': 'IV',
  '\u2164': 'V',    '\u2165': 'VI',   '\u2166': 'VII', '\u2167': 'VIII',
  '\u2168': 'IX',   '\u2169': 'X',    '\u216A': 'XI',  '\u216B': 'XII',
  // Geometric shapes / dingbats
  '\u25CF': '*', '\u25CB': 'o', '\u25A0': '[#]', '\u25A1': '[ ]',
  '\u2713': 'v', '\u2714': 'v', '\u2717': 'x',  '\u2718': 'x',
  '\u27A1': '->',
  // Invisible / zero-width
  '\u200B': '', '\u200C': '', '\u200D': '', '\u2060': '', '\uFEFF': '',
  '\u00AD': '', '\u202F': ' ', '\u205F': ' ', '\u3000': ' ',
  // Primes → apostrophes
  '\u2032': "'", '\u2033': "''", '\u2034': "'''",
  // Geometric shapes
  '\u2666': '<>', '\u2665': '<3', '\u2663': '+', '\u2660': '+',
};

function breakLongUnbrokenRuns(text, maxRun = 48) {
  return String(text || '').replace(new RegExp(`(\\S{${maxRun},}})`, 'g'), (run) => {
    const parts = run.match(new RegExp(`.{1,${maxRun}}`, 'g')) || [run];
    return parts.join(' ');
  });
}

/**
 * Normalize text to the Times-Roman/WinAnsiEncoding character set.
 * Returns a string safe to pass to @react-pdf/renderer with no custom fonts.
 */
export function normalizeForNativePdf(text) {
  if (!text) return '';
  let s = String(text);

  // 1. Named HTML entities
  s = s.replace(ENTITY_PATTERN, (match) => {
    const lower = match.toLowerCase();
    return HTML_ENTITY_MAP[lower] ?? HTML_ENTITY_MAP[match] ?? match;
  });

  // 2. Numeric HTML entities
  s = decodeRemainingNumericEntities(s);

  // 2b. Common ASCII comparison operators in clinical text (e.g. "CPS >=1")
  s = s.replace(/(\d)\s*>=\s*(\d)/g, '$1\u2265$2');
  s = s.replace(/(\d)\s*<=\s*(\d)/g, '$1\u2264$2');
  s = s.replace(/(\d)\s*>=\s*(?=[A-Za-z%])/g, '$1\u2265 ');
  s = s.replace(/(\d)\s*<=\s*(?=[A-Za-z%])/g, '$1\u2264 ');

  // 3. Mojibake sequences (must run before single-char replacement)
  for (const [bad, good] of Object.entries(MOJIBAKE_MAP)) {
    s = s.split(bad).join(good);
  }

  // 4. Per-character normalization — only WinAnsi-safe output
  let out = '';
  for (const ch of s) {
    const code = ch.codePointAt(0);

    if (code === 0x0a || code === 0x0d || code === 0x09) {
      out += ' ';
      continue;
    }
    if (code < 0x20) continue;

    if (WINANSI_SINGLE_BYTE.has(code)) {
      out += ch;
      continue;
    }

    if (WINANSI_EXTRA_CODEPOINTS.has(code)) {
      out += ch;
      continue;
    }

    if (PDF_UNICODE_GLYPHS.has(code)) {
      out += ch;
      continue;
    }

    const mapped = UNICODE_FALLBACK_MAP[ch];
    if (mapped !== undefined) {
      out += mapped;
      continue;
    }

    if (code >= 0x0100 && code <= 0x017e) {
      const normalized = ch.normalize('NFD').replace(/[\u0300-\u036F]/g, '');
      for (const base of normalized) {
        const bc = base.codePointAt(0);
        if (WINANSI_SINGLE_BYTE.has(bc)) out += base;
      }
      continue;
    }
  }

  // 5. Collapse repeated whitespace; break very long unbroken tokens for PDF line wrapping.
  // Do not trim — trimming per-run text removes intentional leading spaces between runs.
  return breakLongUnbrokenRuns(out.replace(/[ \t\u00A0]+/g, ' '));
}

const UNICODE_GLYPH_ASCII_FALLBACK = {
  '\u2264': '<=',
  '\u2265': '>=',
  '\u2260': '!=',
  '\u2248': '~',
  '\u2212': '-',
};

/** When the embedded Unicode font is unavailable, map math glyphs to WinAnsi-safe ASCII. */
export function coerceUnicodeGlyphsToAscii(text) {
  let s = String(text ?? '');
  for (const [glyph, ascii] of Object.entries(UNICODE_GLYPH_ASCII_FALLBACK)) {
    if (s.includes(glyph)) s = s.split(glyph).join(ascii);
  }
  return s;
}

/**
 * Split normalized text into Times-Roman vs Unicode font segments for hybrid PDF rendering.
 */
export function splitPdfTextFontSegments(text) {
  const input = String(text ?? '');
  if (!input) return [{ kind: 'regular', text: '' }];

  const segments = [];
  let buffer = '';
  let currentKind = null;

  const flush = () => {
    if (!buffer) return;
    segments.push({ kind: currentKind || 'regular', text: buffer });
    buffer = '';
  };

  for (const ch of input) {
    const code = ch.codePointAt(0);
    const kind = PDF_UNICODE_GLYPHS.has(code) ? 'unicode' : 'regular';
    if (currentKind != null && kind !== currentKind) {
      flush();
    }
    currentKind = kind;
    buffer += ch;
  }

  flush();
  return segments.length ? segments : [{ kind: 'regular', text: input }];
}
